import { createDate } from '@viastud/ui/lib/date-service'
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react'
import type React from 'react'

import { cn } from '@/lib/utils.ts'

interface CalendarDay {
  fullDate: string
  dayName: string
  dayNumber: number
}

interface CalendarSlot {
  time: string
  available: boolean
  slotIds: number[]
  professorsAvailabilityIds: number[]
}

interface CalendarPickerProps {
  rangeLabel: string
  days: CalendarDay[]
  slots: CalendarSlot[]
  selectedDate: string | null
  selectedTime: string | null
  loading?: boolean
  onPrev: () => void
  onNext: () => void
  onSelectDate: (date: string) => void
  onSelectTime: (time: string | null) => void
}

export const CalendarPicker: React.FC<CalendarPickerProps> = ({
  rangeLabel,
  days,
  slots,
  selectedDate,
  selectedTime,
  loading = false,
  onPrev,
  onNext,
  onSelectDate,
  onSelectTime,
}) => {
  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <button onClick={onPrev} className="rounded-md p-2 hover:bg-gray-100">
          <ChevronLeft />
        </button>
        <span className="text-sm font-medium">{rangeLabel}</span>
        <button onClick={onNext} className="rounded-md p-2 hover:bg-gray-100">
          <ChevronRight />
        </button>
      </div>

      {/* Days */}
      <div className="mb-6 grid grid-cols-7 gap-2">
        {days.map((day) => {
          const active = selectedDate === day.fullDate
          const isPast = createDate(day.fullDate).isBefore(createDate(), 'day')

          return (
            <button
              key={day.fullDate}
              disabled={isPast}
              onClick={() => {
                if (!isPast) {
                  onSelectDate(day.fullDate)
                  onSelectTime(null)
                }
              }}
              className={cn(
                'flex flex-col items-center p-2 text-sm transition',
                isPast && 'cursor-not-allowed opacity-40'
              )}
            >
              <span className="mb-1 text-sm capitalize">{day.dayName}</span>
              <span
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-lg font-semibold',
                  active && 'blue-gradiant text-white'
                )}
              >
                {day.dayNumber}
              </span>
            </button>
          )
        })}
      </div>

      {/* Slots */}
      <div className="mb-6 grid min-h-[7em] grid-cols-4 gap-3">
        {loading && <p className="text-sm text-gray-500">Chargement…</p>}

        {!loading && slots.length === 0 && (
          <p className="col-span-4 text-sm text-gray-400">Aucun créneau disponible pour ce jour.</p>
        )}

        {slots.map((slot) => {
          const active = selectedTime === slot.time

          return (
            <button
              key={slot.time}
              disabled={!slot.available}
              onClick={() => {
                onSelectTime(slot.time)
              }}
              className={cn(
                'flex h-12 max-h-[50px] w-full items-center justify-center gap-2 rounded-lg border px-4 py-3 transition',
                active
                  ? 'blue-gradiant border-gray-400 text-white'
                  : 'border-gray-200 hover:bg-gray-50',
                !slot.available && 'cursor-not-allowed opacity-40'
              )}
            >
              <Clock size={16} />
              {slot.time}
            </button>
          )
        })}
      </div>
    </div>
  )
}
